import React from 'react';
import { useTranslation } from 'react-i18next';

import { Icon, Panel, Text } from '../../../../components';
import { CASINO_SETTINGS, COLORS } from '../../../../theme/tokens';
import { PROVISION_ROWS } from '../../provisioning';
import {
  Screen,
  FormTitle,
  Intro,
  Lead,
  Checklist,
  Row,
  Glyph,
  Dot,
  Spinner,
  RowLabel,
  RowStatus,
  FailedBody,
  FailureNote,
  TextLink,
  type RowState,
} from './ProvisioningView.styles';

export type ProvisioningViewProps = {
  /** The full address being set up (`sales@{domain}`). */
  address: string;
  /**
   * The single index the rows render from: rows `1..step` are done, row `step + 1` is in progress,
   * the rest pending; `PROVISION_ROWS` means all five are done. Every row is PACED, row 1 included:
   * the screen appears on the click, row 1 completes about a second in while the create is still in
   * flight, and the mailbox does not exist until the create answers (the last row waits for that
   * answer). An equal rhythm was chosen over a first row that stretches. MX and SPF tick whether or
   * not the zone has verified yet (see `provisioning.ts`). The section computes it (see
   * `useProvisioningStep`).
   */
  step: number;
  /** Setup failed: the row in progress (`step`) shows a warning, the rest stay pending. */
  failed: boolean;
  /** Why it failed, from the provider (rendered as a value, escaped). */
  failureReason?: string;
  /** Try again: the section re-reads the domain. It never re-sends the create. A failed create never
   *  reaches this screen's failure branch: it returns to the form. */
  onRetry: () => void;
};

/** One row's state from the step index. Done rows stay done: this is a record, not a progress bar. */
const rowState = (index: number, step: number, failed: boolean): RowState => {
  if (index < step) return 'done';
  if (index === step) return failed ? 'failed' : 'active';
  return 'pending';
};

type ChecklistRowProps = {
  /** The row's position (0-based) in the step order. */
  index: number;
  step: number;
  failed: boolean;
  /** The row's copy: a literal at each call site, so the extractor harvests all five. */
  text: string;
};

/**
 * One row: its glyph, its copy, and a visually hidden state word. `data-state` is a deliberate test
 * hook (the glyph is the only visual difference). The glyph is decorative (aria-hidden, so the
 * spinner is never announced as an image); the state word is what a screen reader hears, and its
 * change is what the live checklist announces as each row completes.
 */
const ChecklistRow = ({ index, step, failed, text }: ChecklistRowProps): React.ReactElement => {
  const state = rowState(index, step, failed);
  return (
    <Row data-state={state}>
      <Glyph aria-hidden="true">{glyph(state)}</Glyph>
      <RowLabel text={text} tag="span" $state={state} />
      {/* Four existing catalog keys, one static literal each. */}
      {state === 'done' ? (
        <RowStatus text="Done" tag="span" />
      ) : state === 'active' ? (
        <RowStatus text="In progress" tag="span" />
      ) : state === 'failed' ? (
        <RowStatus text="Failed" tag="span" />
      ) : (
        <RowStatus text="Pending" tag="span" />
      )}
    </Row>
  );
};

const glyph = (state: RowState): React.ReactElement => {
  switch (state) {
    case 'pending':
      return <Dot />;
    case 'active':
      return <Spinner />;
    case 'done':
      return <Icon name="check" size={16} color={CASINO_SETTINGS.okFg} />;
    case 'failed':
      return <Icon name="warning" size={16} color={COLORS.warning} />;
    default: {
      const unhandled: never = state;
      return unhandled;
    }
  }
};

/**
 * The provisioning screen: what the section shows from the click on a FIRST create (or, when billed,
 * from its settled payment) to the mailbox's page, which it moves to on its own once the last row
 * completes and the create has answered. No button. The create runs behind it and does the DNS work;
 * this makes that visible, row by row. A create that fails returns to the form instead. ALL FIVE
 * rows are paced and none waits for DNS: MX and SPF tick whether or not the zone has verified. That is
 * the deliberate honesty gap, and the original screen's own bargain: the truth about DNS is the
 * section's setting-up notice ("Incoming mail is on its way"), which keeps saying it. Pure: everything it
 * shows comes from props, and it holds NOTHING the lifecycle needs. The lead is shown from the first
 * frame and dropped only beside a failure, where "setup continues without you" would not be true. A later mailbox never sees it:
 * its create touched no DNS, and five rows about DNS records would be a lie.
 */
export const ProvisioningView = ({
  address,
  step,
  failed,
  failureReason,
  onRetry,
}: ProvisioningViewProps): React.ReactElement => {
  const { t } = useTranslation();
  const current = Math.max(0, Math.min(PROVISION_ROWS, step));

  return (
    <Panel>
      <Screen>
        {/* The title and its lead, one group. The lead shows from the first frame, but NOT beside a
            failure: "setup continues without you" would be a false promise next to the provider's
            reason, so the failure branch drops it (the failure body below says what happened). */}
        <Intro>
          <FormTitle title={t('Setting up {{address}}', { address })} />
          {failed ? null : (
            <Lead text="This takes a moment. You can leave this page; setup continues without you." tag="span" />
          )}
        </Intro>

        {/* Row 1 is the mailbox; rows 2 to 5 are its DNS records. Five literals, in step order. A polite
            live region, busy while setup runs, so each completion is heard rather than silent. */}
        <Checklist aria-live="polite" aria-busy={!failed && current < PROVISION_ROWS}>
          <ChecklistRow index={0} step={current} failed={failed} text="Creating the mailbox" />
          <ChecklistRow index={1} step={current} failed={failed} text="MX record, so mail reaches it" />
          <ChecklistRow index={2} step={current} failed={failed} text="SPF record, so your mail is authorised" />
          <ChecklistRow index={3} step={current} failed={failed} text="DKIM record, so your mail is signed" />
          <ChecklistRow index={4} step={current} failed={failed} text="DMARC policy, so receivers know what to trust" />
          {/* TODO(autodiscover): the original's sixth row, mail clients configuring themselves, returns
              here, in this position, when the service publishes the records for it. The create
              publishes four records today (MX, SPF, DKIM, DMARC) and nothing for client
              self-configuration: tested on a live mailbox, Thunderbird filled in nothing and Outlook
              hung. A row for something that never completes would spin forever or lie. */}
        </Checklist>

        {failed ? (
          <FailedBody>
            {failureReason ? <FailureNote text={failureReason} tag="span" /> : null}
            <TextLink type="button" onClick={onRetry}>
              <Text text="Try again" tag="span" />
            </TextLink>
          </FailedBody>
        ) : null}
      </Screen>
    </Panel>
  );
};

export default ProvisioningView;
